import { useEffect, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { MessageCircle, RotateCcw, Send } from "lucide-react";
import { toast } from "sonner";
import { openWhatsApp } from "@/lib/whatsappTemplates";
import api from "@/lib/api";

/**
 * Admin one-click "Send booking to WhatsApp" button.
 *
 * Opens a popover with a pre-composed confirmation template (ref, driver,
 * pickup date/time, Google Maps pickup link, vehicle). The concierge can
 * tweak the copy before firing the WhatsApp deep-link. On send we POST a
 * lightweight audit entry to `/admin/bookings/{id}/whatsapp-log` so it
 * shows up in the booking's `whatsapp_log` timeline.
 */
export function SendBookingWhatsAppButton({ booking, defaultMessage }) {
  const disabled = !booking?.phone;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(defaultMessage || "");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setDraft(defaultMessage || "");
  }, [defaultMessage]);

  const send = async () => {
    const trimmed = (draft || "").trim();
    if (!trimmed) {
      toast.error("Message can't be empty");
      return;
    }
    setSending(true);
    const url = openWhatsApp(booking.phone, trimmed);
    try {
      await api.post(`/admin/bookings/${booking.id}/whatsapp-log`, {
        event: "admin_send_booking",
        mode: "deeplink",
        success: true,
        url,
      });
    } catch (err) {
      // Non-blocking — the WhatsApp tab already opened.
      console.warn("WhatsApp audit log failed", err);
    }
    setSending(false);
    toast.success("WhatsApp opened", {
      description: `Confirmation drafted for ${booking.name?.split(" ")[0] || "the customer"}.`,
    });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`send-booking-whatsapp-btn-${booking?.id}`}
          disabled={disabled}
          title={disabled ? "No customer phone on file" : "Send booking confirmation via WhatsApp"}
          className="h-9 px-4 rounded-full border border-emerald-500/50 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-100 text-[11px] uppercase tracking-[0.15em] flex items-center gap-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <MessageCircle className="w-3.5 h-3.5" />
          Send to WhatsApp
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="left"
        sideOffset={8}
        className="w-[360px] sm:w-[440px] p-0 bg-zinc-950 border border-white/10 shadow-2xl"
        data-testid={`send-booking-whatsapp-popover-${booking?.id}`}
      >
        <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-emerald-300/80">
              WhatsApp preview
            </p>
            <p className="text-sm text-zinc-100 font-light mt-0.5">
              Booking confirmation
              {booking?.name ? (
                <span className="text-zinc-500"> · {booking.name.split(" ")[0]}</span>
              ) : null}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setDraft(defaultMessage || "")}
            title="Reset to template"
            data-testid={`send-booking-whatsapp-reset-${booking?.id}`}
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-200 transition"
          >
            <RotateCcw className="w-3 h-3" />
            Reset
          </button>
        </div>
        <div className="p-4 space-y-3">
          <Textarea
            data-testid={`send-booking-whatsapp-textarea-${booking?.id}`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={12}
            className="w-full text-sm bg-zinc-900 border-white/10 text-zinc-100 focus-visible:ring-emerald-500/40 resize-none leading-relaxed"
          />
          <div className="flex items-center justify-between text-[10px] text-zinc-500">
            <span>{draft.length} chars</span>
            <span>Opens WhatsApp in a new tab</span>
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-[11px] uppercase tracking-widest text-zinc-400 hover:text-zinc-200 px-3 py-1.5"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid={`send-booking-whatsapp-confirm-${booking?.id}`}
              onClick={send}
              disabled={sending}
              className="inline-flex items-center gap-1.5 h-8 px-4 rounded-full border border-emerald-500/50 bg-emerald-500/20 text-emerald-100 text-[11px] uppercase tracking-widest hover:bg-emerald-500/30 transition disabled:opacity-50"
            >
              <Send className="w-3 h-3" />
              {sending ? "Opening…" : "Send via WhatsApp"}
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
